import React, { useState } from "react";
import { Loader2, Upload, X, Download } from "lucide-react";
import { api } from "@/lib/api";

export async function downloadProject(path, title, headers) {
  const r = await api.get(path, { headers, responseType: "blob", timeout: 600000 });
  const cd = r.headers?.["content-disposition"] || "";
  const m = cd.match(/filename="([^"]+)"/);
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = m ? m[1] : `${title || "showo-film"}.mp4`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function DownloadButton({ path, title, headers, testId, className = "", label = "Download" }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await downloadProject(path, title, headers()); }
    catch (e) {
      let msg = "Download failed";
      try { msg = JSON.parse(await e?.response?.data?.text?.())?.detail || msg; } catch (_) {}
      alert(msg);
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={run} disabled={busy} className={`fl-btn ghost !py-1.5 !px-3 text-xs ${className}`} data-testid={testId}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} {busy ? "…" : label}
    </button>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">{label}</span>
    {children}
  </label>
);

export function EditProjectModal({ project, categories, headers, onSave, onClose }) {
  const [form, setForm] = useState({
    title: project.title || "", synopsis: project.synopsis || "",
    genre: project.genre || "", category: project.category || "", cover_url: project.cover_url || "",
  });
  const [busy, setBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const uploadCover = async (file) => {
    if (!file) return;
    setCoverBusy(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/images/upload", fd, { headers: { ...headers(), "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, cover_url: r.data.url }));
    } catch (e) { setErr(e?.response?.data?.detail || "Cover upload failed"); }
    finally { setCoverBusy(false); }
  };

  const save = async () => {
    if (form.title.trim().length < 2) { setErr("Title is required"); return; }
    setBusy(true); setErr("");
    const patch = { title: form.title.trim(), synopsis: form.synopsis, genre: form.genre, cover_url: form.cover_url };
    if (categories) patch.category = form.category;
    try { await onSave(patch); onClose(); }
    catch (e) { setErr(e?.response?.data?.detail?.toString?.() || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" data-testid="showo-edit-modal">
      <div className="fl-card p-6 bg-[#0b0c1a] border-white/15 max-w-md w-full space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="fl-display text-lg font-black">Edit project</div>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white" data-testid="showo-edit-close"><X className="w-4 h-4" /></button>
        </div>
        <Field label="Title"><input value={form.title} onChange={set("title")} maxLength={140} className="fl-input w-full mt-1" data-testid="showo-edit-title" /></Field>
        <Field label="Description"><textarea value={form.synopsis} onChange={set("synopsis")} maxLength={2000} className="fl-input w-full mt-1 min-h-[90px]" data-testid="showo-edit-synopsis" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Genre"><input value={form.genre} onChange={set("genre")} maxLength={60} className="fl-input w-full mt-1" data-testid="showo-edit-genre" /></Field>
          {categories && (
            <Field label="Category">
              <select value={form.category} onChange={set("category")} className="fl-input w-full mt-1" data-testid="showo-edit-category">
                {!categories.some(c => c.id === form.category) && form.category && <option value={form.category}>{form.category}</option>}
                {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </Field>
          )}
        </div>
        <Field label="Cover image">
          <div className="mt-1 flex items-center gap-3">
            {form.cover_url ? <img src={form.cover_url} alt="" className="w-14 h-20 object-cover rounded border border-white/10" data-testid="showo-edit-cover-preview" /> : <div className="w-14 h-20 rounded bg-white/5 border border-white/10" />}
            <span className="fl-btn ghost !py-1.5 !px-3 text-xs cursor-pointer" data-testid="showo-edit-cover-btn">
              {coverBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {coverBusy ? "Uploading…" : "Change cover"}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={e => uploadCover(e.target.files?.[0])} data-testid="showo-edit-cover-input" />
            </span>
          </div>
        </Field>
        {err && <div className="text-red-300 text-sm" data-testid="showo-edit-err">{err}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="showo-edit-cancel">Cancel</button>
          <button type="button" onClick={save} disabled={busy || coverBusy} className="fl-btn !bg-emerald-500 !text-black !py-1.5 !px-3 text-xs" data-testid="showo-edit-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save changes
          </button>
        </div>
      </div>
    </div>
  );
}
